import { action, observable, reaction, runInAction } from 'mobx';
import { toastr } from 'react-redux-toastr';

import APPConf from 'lib/config/common';
import controller from './controller';
import {
  BonusHistoryInterface, ClientInsuranceItem, ClientProfile, ClientTypeDestination,
  ClientProfileWithOptionalId, ClientFCMTokensListInterface,
} from '../api/clientApi.d';

export interface ClientShortSearchView {
  clientId: string,
  fullName: string,
}

export interface ClientStore {
  clientProfile: ClientProfile,
  clientProfileByClientId: string,
  clientsFoundByName: ClientShortSearchView[],
  error: string,
  bonusHistory: BonusHistoryInterface[],
  bonusHistoryByClientId: string, // по данному клиенту находятся данные в bonusHistory
  currentBonusHistory: BonusHistoryInterface,

  clientInsurances: ClientInsuranceItem[],
  clientInsurancesByClientId: string,

  // далее блок для заявок, когда надо показать 2 и более клиента за ра
  // ищи в коде insurantClientId или vehicleOwnerClientId
  insurantClientProfile: ClientProfile,
  vehicleOwnerClientProfile: ClientProfile,

    clientFCMTokensList: ClientFCMTokensListInterface[],
    clientFCMTokensByClientId: string,
  
}

const clientStoreInitialValues: ClientStore = {
  clientProfile: null,
  clientProfileByClientId: null,
  clientsFoundByName: null,
  error: null,
  bonusHistory: [],
  bonusHistoryByClientId: null,
  currentBonusHistory: {},

  clientInsurances: [],
  clientInsurancesByClientId: null,

  insurantClientProfile: null,
  vehicleOwnerClientProfile: null,

  clientFCMTokensList: [],
  clientFCMTokensByClientId: null,
  
};

export const clientStore = observable(clientStoreInitialValues);
export const clientActions = {

  getClientProfileByPhone: async (phone: string, clientType: ClientTypeDestination='primary'):Promise<ClientProfile> => {
    try {
      const response = await controller.findClientByPhone(phone);
      if(response?.error) { 
        // clientActions.handleError(response);
        clientActions.clearClientProfile(clientType);
        return null;
      }
      clientActions.setClientByDestination(response?.data, clientType);
      return response.data;
    } catch (err) {
      clientStore.error = err.message || err;
      return null;
    } 
  },

  getClientProfileById: async (id: string, clientType: ClientTypeDestination='primary'):Promise<ClientProfile> => {
    if (clientType === 'primary' && clientStore.clientProfileByClientId === id) { return };
    try {
      runInAction(() => {
        clientStore.clientProfileByClientId = id;
      });
      const response = await controller.getClient(id);
      clientActions.setClientByDestination(response?.data, clientType);
      return response?.data;
    } catch (err) {
      clientStore.error = err.message || err;
      return null;
    }
  },

  setClientByDestination: action((clientProfile: ClientProfile, clientType: ClientTypeDestination='primary') => {
    switch (clientType) {
      case 'insurant':
        clientStore.insurantClientProfile = clientProfile;
        break;
      case 'vehicleOwner':
        clientStore.vehicleOwnerClientProfile = clientProfile;
        break;        
      default:
        clientStore.clientProfile = clientProfile;
        break;
    }
  }),

  createClientInMobx: async (request: ClientProfileWithOptionalId, clientType: ClientTypeDestination='primary'): Promise<ClientProfile> => {
    const response = await controller.createClient(request);
    clientActions.handleError(response);
    clientActions.setClientByDestination(response?.data, clientType);
    return response?.data;
  },
  
  updateClientInMobx: async (request: ClientProfileWithOptionalId, clientType: ClientTypeDestination='primary'): Promise<ClientProfile> => {
    const response = await controller.updateClient(request);
    clientActions. handleError(response);
    clientActions.setClientByDestination(response?.data, clientType);
    return response?.data;
  },

  deleteClientInMobx: async (request: string, clientType: ClientTypeDestination='primary'): Promise<void> => {
    const response = await controller.deleteClient(request);
    clientActions.clearClientProfile(clientType);
    clientActions.handleError(response);
  },

  handleError: action((response) => {
    if (response.errorData) {
      const { data } = response;
      clientStore.error = data;
      throw data;
    }
  }),

  clearClientProfile: async (clientType: ClientTypeDestination='primary') => {
    runInAction(() => {
      switch (clientType) {
        case 'insurant':
          clientStore.insurantClientProfile = null;
          break;
        case 'vehicleOwner':
          clientStore.vehicleOwnerClientProfile = null;
          break;
        default:
          clientStore.clientProfile = null;
          // Сбрасываем кэш id, иначе getClientProfileById уйдёт в ранний return
          // и не перечитает профиль после очистки (мелькание/пропадание ФИО при повторном открытии).
          clientStore.clientProfileByClientId = null;
          break;
      }
    });
  },

  getClientProfileByDestination: (clientType: ClientTypeDestination='primary'): ClientProfile => {
    switch (clientType) {
      case 'insurant':
        return clientStore.insurantClientProfile;            
        break;
      case 'vehicleOwner':
        return clientStore.vehicleOwnerClientProfile;            
        break;        
      default:
        return clientStore.clientProfile;
        break;
    }
  },

  findClientsByName: async ({ firstName, lastName }) => {
    try {
      const response = await controller.findClientsByName({ firstName, lastName });
      runInAction(() => {
        clientStore.clientsFoundByName = response.data;
      });
    } catch (err) {
      clientStore.error = err.message || err;
    }
  },

  clearClientsFoundByName: action(() => {
    clientStore.clientsFoundByName = null;
  }),

  getBonusHistory: async (clientId) => {
    try {
      runInAction(() => {
        clientStore.bonusHistoryByClientId = clientId;
      });
      const response = await controller.getBonusHistory(clientId);
      runInAction(() => {
        clientStore.bonusHistory = response.data.sort((a, b) => {
          return (a.createdDate > b.createdDate) ? -1 : 1;
        });
      });
    } catch (err) {
      clientStore.error = err.message || err;
    }
  },

  createClientBonus: async (data) => {
    const response = await controller.createClientBonus(data);
    runInAction(() => {
      if (response.errorData) {
        clientStore.error = response.errorData.message;
      } else if (data?.operationTypeRefValueId === 345) {
        toastr.success('Бонусы', 'успешно начислены');
      } else {
        toastr.success('Бонусы', 'успешно списаны');
      }
    });
  },

  clearError: async () => {
    runInAction(() => {
      clientStore.error = null;
    });
  },

  getClientInsurances: async (clientId:string) => {
    try {
      runInAction(() => {
        clientStore.clientInsurancesByClientId = clientId;
      });
      const response = await controller.getClientInsurances(clientId);
      runInAction(() => {
        clientStore.clientInsurances = response.data.items;
      });
    } catch (err) {
      clientStore.error = err.message || err;
    }
  },

  clearClientInsurances: async () => {
    runInAction(() => {
      clientStore.clientInsurancesByClientId = null;
      clientStore.clientInsurances = []; // response.data.items;
    });
  },

  getClientFCMTokensList: async (clientId:string): Promise<void> => {
    clientStore.error = null;
    runInAction(() => {
      clientStore.clientFCMTokensByClientId = clientId;
    });
    const response = await controller.getClientFCMTokensList(clientId);
    clientActions.handleError(response);
    if (!response.errorData) {
      runInAction(() => {
        clientStore.clientFCMTokensList = response?.data?.items ?? [];
      });
    } else {
      clientStore.clientFCMTokensByClientId = undefined;
      clientStore.clientFCMTokensList = [];
    }
  },

  clearClientFCMTokensList: action(() => {
    clientStore.clientFCMTokensList = [];
    clientStore.clientFCMTokensByClientId = null;
  }),

  changeClient: async (orderId: string, clientId: string): Promise<boolean> => {
    const response = await controller.changeClient(orderId, clientId);
    if (response?.errorData) {
      clientActions.handleError(response);
      return false;
    }
    return true;
  },

};

reaction(() => clientStore.error, () => {
  toastr.error(APPConf.errorHdr, clientStore.error);
});
